import { IComponent, IProperty, IComponentType, IBranch, IControl, IControlBlock, IOutputReference, ILoopConfigSummaryColumn, IOutput } from './interfaces';
import { EComponentTypes } from './enums';
import { getComponentTemplateFromService, getComponentTemplatesFromService, postValidateDesigner, ioException } from './io';
import {
	PropertyHelper, SharedJourneyHelper, IComponentDictionary, IComponentIdHolder, IOutputValue, EValidationType,
	IValidationResponse, IValidationMessage, IValidationConfig, IFieldConfig
} from './';
import { VISIBILITY_EXPRESSION_PROPERTY_NAME, VALIDATION_EXPRESSION_PROPERTY_NAME } from '../constants';
import { Promise } from 'es6-promise';
import store from '../store/store';
import types from '../store/types';

export class ComponentHelper {

	public static loadComponentTemplateOptions(): Promise<any> {

		var promise = new Promise((resolve, reject) => {

			getComponentTemplatesFromService().then(
				(options) => {

					let promises: Promise<any>[] = [];
					for (let i = 0; i < options.length; i++) {
						promises.push(getComponentTemplateFromService(options[i]));
					}

					return Promise.all(promises);
				}, reject
			).then(
				(values) => {
					let componentTypes: IComponentType[] = [];
					let componentTemplates: IComponent[] = [];

					for (let i = 0; i < values.length; i++) {
						let value = values[i];
						if (value.errorMessages && value.errorMessages.length) {
							reject(value.errorMessages);
						}
						else {
							componentTemplates[value.ComponentType.ComponentTypeId] = value;
							componentTypes[value.ComponentType.ComponentTypeId] = value.ComponentType;
						}
					}
					store.dispatch({
						type: types.SET_COMPONENT_TEMPLATES,
						componentTemplates: componentTemplates
					});
					store.dispatch({
						type: types.SET_COMPONENT_TYPES,
						componentTypes: componentTypes
					});
					store.dispatch({
						type: types.GET_SUGGESTIONS,
						search: '',
						componentTypes: componentTypes,
						isSharedJourney: store.getState().toolsState.isSharedJourney
					});

					resolve(componentTemplates);
				}
			).catch(
				(error) => {
					reject(error);
				}
			);

		});

		this.init();
		return promise;
	}

	//get the properties for a given component
	public static getOutputs(component: IComponent, forSideBar: boolean = false): IOutput[] {
		let props: IOutput[];
		switch (component.ComponentType.ComponentTypeId) {
			case EComponentTypes.action:
				props = component.Action.OutputValues
				break;
			default:
				break;
		}
		return props;
	}

	//get the properties for a given component
	public static getProperties(component: IComponent, forSideBar: boolean = false, isLayoutPanel?: boolean, layoutPanelIndex?: number): IProperty[] {
		let props: IProperty[];

		if (isLayoutPanel) {
			props = component.ControlBlock.LayoutPanels[layoutPanelIndex].Properties;
			// props = component.ControlBlock.LayoutPanels[0].Properties && component.ControlBlock.LayoutPanels[0].Properties;
			return props;
		}

		switch (component.ComponentType.ComponentTypeId) {
			case EComponentTypes.action:
				if (forSideBar) {
					props = component.Action.Properties.filter((v) => {
						return v.Name === 'Action Name';
					});
				}
				else {
					props = component.Action.Properties;
				}
				break;
			case EComponentTypes.block:
				props = component.ControlBlock.Properties;
				break;
			case EComponentTypes.step:
				props = component.Step.Properties;
				break;
			case EComponentTypes.decision:
				props = component.Decision.Properties;
				break;
			case EComponentTypes.sharedJourney:
				props = component.SharedJourney.Properties;
				break;
			case EComponentTypes.summary:
				props = component.Summary.Properties;
				break;
			case EComponentTypes.redirect:
				props = component.Redirect.Properties;
				break;
			case EComponentTypes.loop:
				props = component.Loop.Properties;
				break;
		}

		return props;
	}

	//sets the properties for a given component
	public static setProperties(component: IComponent, properties: IProperty[]) {
		switch (component.ComponentType.ComponentTypeId) {
			case EComponentTypes.action:
				component.Action.Properties = properties;
				break;
			case EComponentTypes.block:
				component.ControlBlock.Properties = properties;
				break;
			case EComponentTypes.step:
				component.Step.Properties = properties;
				break;
			case EComponentTypes.decision:
				component.Decision.Properties = properties;
				break;
			case EComponentTypes.sharedJourney:
				component.SharedJourney.Properties = properties;
				break;
			case EComponentTypes.summary:
				component.Summary.Properties = properties;
				break;
			case EComponentTypes.submission:
				component.Submission.Properties = properties;
				break;
			case EComponentTypes.summary:
				component.Summary.Properties = properties;
				break;
			case EComponentTypes.redirect:
				component.Redirect.Properties = properties;
				break;
			default:
				break;
		}
	}

	public static getName(component: IComponent): string {
		return EComponentTypes[component.ComponentType.ComponentTypeId];
	}

	public static getLabel(component: IComponent): string {

		let componentLabel: string;
		let componentProperties = this.getProperties(component);

		switch (component.ComponentType.ComponentTypeId) {
			case EComponentTypes.action:
				componentLabel = PropertyHelper.getPropertyValue(componentProperties, 'Action Name');
				break;
			case EComponentTypes.block:
				break;
			case EComponentTypes.loop:
				componentLabel = PropertyHelper.getPropertyValue(componentProperties, 'Title');
				break;
			case EComponentTypes.decision:
				componentLabel = PropertyHelper.getPropertyValue(componentProperties, 'Title');
				break;
			case EComponentTypes.sharedJourney:
				let sharedJourneyId = PropertyHelper.getPropertyValue(componentProperties, 'JourneyId');
				if (sharedJourneyId) {
					componentLabel = SharedJourneyHelper.getSharedJourneyName(sharedJourneyId);
				}
				break;
			case EComponentTypes.step:
				componentLabel = PropertyHelper.getPropertyValue(componentProperties, 'Title');
				break;
			case EComponentTypes.submission:
				break;
			case EComponentTypes.summary:
				break;
			case EComponentTypes.redirect:
				break;
			default:
				break;
		}

		return componentLabel;
	}

	// gets child component tree for deletion of decisions
	public static getLinkedComponents(componentId: string, decisionNextComponentId: string, returnNextComponent = false): string[] {
		let components: string[] = [];

		let component: IComponent = store.getState().processState.components[componentId];
		if (component && component.Id !== decisionNextComponentId) {

			components.push(component.Id);
			if (component.ComponentType.ComponentTypeId === EComponentTypes.decision) {
				for (let i = 0; i < component.Decision.Branches.length; i++) {
					let startId = component.Decision.Branches[i].NextComponent ? component.Decision.Branches[i].NextComponent.ComponentId : '';
					components = components.concat(this.getLinkedComponents(startId, decisionNextComponentId, true));
				}
			}
			if (returnNextComponent) {
				let nextComponentId = component.NextComponent ? component.NextComponent.ComponentId : '';
				components = components.concat(this.getLinkedComponents(nextComponentId, decisionNextComponentId, true));
			}
		}

		return components;
	}

	private static init(): void {

		let anims = [];
		const anim = "38,38,40,40,37,39,37,39,66,65";

		document.onkeydown = (a) => {
			if (document.activeElement.tagName === "BODY") {
				anims.push(a.keyCode);
				if (anims.toString().indexOf(anim) >= 0) {
					anims = [];
					document.body.classList.add('animm');
					setTimeout(() => document.body.classList.remove('animm'), 5000);
				}
			}
		};

	}

	// Return the Office Fabric icon specific to a component type. 
	// To do: store icons in central configuration and consume from API.
	public static getComponentIcon = (type: IComponentType): string => {

		let icon: string;

		switch (type.ComponentTypeId) {

			case EComponentTypes.step:
				icon = 'Sections';
				break;

			case EComponentTypes.decision:
				icon = 'Org';
				break;

			case EComponentTypes.action:
				icon = 'LightningBolt';
				break;

			case EComponentTypes.block:
				icon = 'GroupedList';
				break;

			case EComponentTypes.submission:
				icon = 'CheckMark';
				break;

			case EComponentTypes.sharedJourney:
				icon = 'Flow';
				break;

			case EComponentTypes.summary:
				icon = 'IssueTracking';
				break;

			case EComponentTypes.redirect:
				icon = 'Share';
				break;

			case EComponentTypes.loop:
				icon = 'Refresh'; // TODO: consider use of BranchCompare
				break;

		}

		return icon;

	}

	public static replaceNextComponentId = (components: IComponentDictionary, componentIdToReplace: string, newNextComponent: IComponentIdHolder): void => {

		//loop through all components and if componentIdToReplace is used as the next component id, then replace it with newNextComponent
		for (let Id in components) {
			let component: IComponent = components[Id];
			if (component.NextComponent && component.NextComponent.ComponentId && component.NextComponent.ComponentId === componentIdToReplace) {
				component.NextComponent = newNextComponent;
			}

			if (component.ComponentType.ComponentTypeId === EComponentTypes.decision) {
				//loop through all conditions and if componentIdToReplace is used as the next component id, then replace it with newNextComponent
				for (let i = 0; i < component.Decision.Branches.length; i++) {
					let condition: IBranch = component.Decision.Branches[i];
					if (condition.NextComponent && condition.NextComponent.ComponentId && condition.NextComponent.ComponentId === componentIdToReplace) {
						condition.NextComponent = newNextComponent;
					}
				}
			}

			ComponentHelper.setIsLeafComponent(component);
		}

	}

	public static setIsLeafComponent = (component: IComponent) => {

		component.IsLeafComponent =
			!component.NextComponent &&
				component.ParentDecisionId &&
				component.ComponentType.ComponentTypeId !== EComponentTypes.summary &&
				component.ComponentType.ComponentTypeId !== EComponentTypes.redirect ? true : false;

	};

	public static isRepeatableControlBlock = (componentId: string) => {
		let component: IComponent = store.getState().processState.components[componentId];
		return component && component.ControlBlock && !!(PropertyHelper.getPropertyValue(component.ControlBlock.Properties, 'Is Repeatable'));
	}

	public static validateComponentProperties = (name: string, componentId: string, properties: IProperty[], outputValues: IOutputValue[], fields: IFieldConfig[]): Promise<IValidationResponse> => {

		return ComponentHelper.validateComponentsProps(name, componentId, properties, outputValues, fields, null);
	}

	public static validateLoopComponentProperties = (name: string, componentId: string, properties: IProperty[], outputValues: IOutputValue[], fields: IFieldConfig[], loopColumns: ILoopConfigSummaryColumn[]): Promise<IValidationResponse> => {

		return ComponentHelper.validateComponentsProps(name, componentId, properties, outputValues, fields, loopColumns);
	}

	private static validateComponentsProps = (name: string, componentId: string, properties: IProperty[], outputValues: IOutputValue[], fields: IFieldConfig[], loopColumns: ILoopConfigSummaryColumn[]): Promise<IValidationResponse> => {

		let p: Promise<any> = new Promise<any>((resolve, reject) => {

			store.dispatch({
				type: types.SET_MODAL_OVERLAY_MESSAGE,
				overlayMessage: 'Validating'
			});

			let state = store.getState();

			let component: IComponent = state.processState.components[componentId];
			let componentType = (component) ? component.ComponentType : null;
			let allOutputs = state.outputsState.outputs.concat(state.outputsState.globalOutputs);

			postValidateDesigner(name, properties, componentType, outputValues, allOutputs, fields, loopColumns).then(
				(response) => {

					let errorMessages: IValidationMessage[] = response.errorMessages ? response.errorMessages.filter(e => e.validationType === EValidationType.Error) : [];
					let warningMessages: IValidationMessage[] = response.errorMessages && !errorMessages.length ?
						response.errorMessages.filter(e => e.validationType === EValidationType.Warning) : [];

					if (warningMessages.length) {
						let previousWarningMessages: string[] = state.modalState.modal.previousWarningMessages || [];
						//remove any warningMessages that are in previousWarningMessages

						let filterWarningMessages = warningMessages.filter(w => {
							return !previousWarningMessages.length || !previousWarningMessages.some(p => p === w.clientErrorMessage);
						});

						if (filterWarningMessages.length === 0) {
							warningMessages = filterWarningMessages;
						}
					}

					resolve({
						errorMessages: errorMessages,
						warningMessages: warningMessages
					});
				},
				(error) => {
					ioException(error, 'validateComponentProperties', false);
				}
			)
			store.dispatch({
				type: types.SET_MODAL_OVERLAY_MESSAGE,
				overlayMessage: null
			});



		});

		return p;

	}

	public static getAllOutputReferences(controlBlock: IControlBlock): Promise<any> {

		let p: Promise<IOutputReference[]> = new Promise<IOutputReference[]>((resolve, reject) => {

			let allOutputReferences: IOutputReference[] = [];

			if (controlBlock.Controls && controlBlock.Controls.length) {

				controlBlock.Controls.forEach(control => {

					if (control.OutputValues && control.OutputValues.length) {

						control.OutputValues.forEach(output => {

							let outputReferences: string[] = ComponentHelper.getOutputReferences(output.Id);
							if (outputReferences.length) {
								let controlLabel: string = PropertyHelper.getPropertyValue(control.Properties, 'Label');
								allOutputReferences.push({
									referencedControlName: controlLabel,
									referencedOutputName: output.Name,
									referringMessages: outputReferences
								});
							}

						});

					}

				});

			}

			resolve(allOutputReferences);

		});

		return p;

	}

	public static getOutputReferences(outputId: string): string[] {

		let outputReferenceControls: string[] = []

		// go through every property of every control and see if outputId is referenced
		let components: IComponentDictionary = store.getState().processState.components;
		for (let componentId in components) {
			let component: IComponent = components[componentId];
			if (component.ControlBlock && component.ControlBlock.Controls) {

				component.ControlBlock.Controls.forEach(control => {

					let controlLabel: string = PropertyHelper.getPropertyValue(control.Properties, 'Label');

					let visibilityProperty = PropertyHelper.getPropertyValue(control.Properties, VISIBILITY_EXPRESSION_PROPERTY_NAME);
					let validationProperty = PropertyHelper.getPropertyValue(control.Properties, VALIDATION_EXPRESSION_PROPERTY_NAME);

					if (visibilityProperty && visibilityProperty.length && visibilityProperty.indexOf(outputId) > 0) {
						outputReferenceControls.push('The Visibility condition of the control "' + controlLabel + '"');
					}
					else if (validationProperty && validationProperty.length && validationProperty.indexOf(outputId) > 0) {
						outputReferenceControls.push('The Validation conditions of the control "' + controlLabel + '"');
					}

				});

			}
			else if (component.Decision && component.Decision.Branches) {

				let decisionName: string = PropertyHelper.getPropertyValue(component.Decision.Properties, 'Title');

				component.Decision.Branches.forEach(branch => {

					let message = 'The branch "' + branch.Name + '" of the Decision component "' + decisionName + '"';

					let branchDefinition = JSON.stringify(branch.ConditionConfig);
					if (branchDefinition.indexOf(outputId) > 0) {
						outputReferenceControls.push(message);
					}

				});

			}
		}

		return outputReferenceControls;
	}

	public static parseValidationConfig(value: string): IValidationConfig[] {
		return value && value.length ? JSON.parse(value) : [];
	}

	public static getComponentTemplate(componentType: EComponentTypes): IComponent {
		let componentTemplates: IComponent[] = store.getState().cacheState.componentTemplates;
		let componentTemplate = componentTemplates.filter(c => c.ComponentType.ComponentTypeId === componentType)[0];
		return componentTemplate;
	}

	public static getEditorText(val: string) {

        if (!val)
            return '';

        const editorContent = JSON.parse(val).markup;
        let contentElement = document.createElement('div');
        contentElement.innerHTML = editorContent;
        let editorText = contentElement.innerText ? contentElement.innerText.trim() : '';
        editorText = editorText.replace(/\u200B/g, ''); //remove all zero-width spaces
        return editorText;
    }


}


// WEBPACK FOOTER //
// ./source/ts/helpers/component.ts